import { useCallback, useEffect, useState } from 'react';
import type { AnnonceResume, MessageIngestion, StatutIngestion } from '@rh/shared';
import { LIBELLES_STATUT_INGESTION, annonceRattachable } from '@rh/shared';
import { libelleAnnonceRattachement } from '../annonces-choix';
import { appel } from '../api';
import { AnnoncesDetectees } from '../composants/AnnoncesDetectees';
import { RelancerLecture } from '../composants/RelancerLecture';
import { Chargement, Erreur, Vide } from '../composants/Etat';

const ONGLETS: Array<{ cle: StatutIngestion | ''; libelle: string }> = [
  { cle: 'a_rattacher', libelle: 'À rattacher' },
  { cle: 'erreur', libelle: 'En erreur' },
  { cle: 'a_traiter', libelle: 'En attente' },
  { cle: 'traite', libelle: 'Traités' },
  { cle: '', libelle: 'Tous' },
];

/**
 * File d'ingestion (docs/API.md § Administration technique) : ce que le worker
 * n'a pas su rattacher, et ce qui a échoué. Le rattachement manuel relance le
 * traitement complet côté worker.
 */
export function Ingestion() {
  const [statut, setStatut] = useState<StatutIngestion | ''>('a_rattacher');
  const [messages, setMessages] = useState<MessageIngestion[] | null>(null);
  const [annonces, setAnnonces] = useState<AnnonceResume[]>([]);
  const [compteurs, setCompteurs] = useState<Record<string, number>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const charger = useCallback(() => {
    setMessages(null);
    appel<MessageIngestion[]>(`/ingestion/messages${statut ? `?statut=${statut}` : ''}`)
      .then(setMessages)
      .catch((e: Error) => setErreur(e.message));
    appel<Record<string, number>>('/ingestion/compteurs').then(setCompteurs).catch(() => undefined);
  }, [statut]);

  // L'API refuse un brouillon et une archivée : la liste ne les propose plus.
  // Une annonce close reste proposée — la candidature est partie avant la
  // clôture — mais son état est écrit à côté de l'intitulé.
  const chargerAnnonces = useCallback(() => {
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => setAnnonces(liste.filter((annonce) => annonceRattachable(annonce.statut))))
      .catch(() => setAnnonces([]));
  }, []);

  useEffect(charger, [charger]);
  useEffect(chargerAnnonces, [chargerAnnonces]);

  async function rattacher(messageId: string, annonceId: string): Promise<void> {
    setInfo(null);
    try {
      await appel(`/ingestion/messages/${messageId}/rattacher`, {
        method: 'POST',
        body: JSON.stringify({ annonceId }),
      });
      // Le worker traite en file : le message ne change pas d'état à l'instant.
      setInfo('Rattachement demandé. Le message sera traité dans quelques instants.');
      setTimeout(charger, 2000);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Rattachement impossible');
    }
  }

  return (
    <section>
      <div className="entete-annonce">
        <h2>File d’ingestion</h2>
        {/* Même composant que la barre de statut : un seul comportement, un
            seul libellé. Ici la liste se recharge aussi, c'est l'écran où l'on
            regarde ce que la relève a ramené. */}
        <RelancerLecture onDemande={charger} />
      </div>

      <AnnoncesDetectees
        apresCreation={() => {
          charger();
          chargerAnnonces();
        }}
      />

      <div className="filtres" role="group" aria-label="Filtrer par état">
        {ONGLETS.map((onglet) => (
          <button
            key={onglet.cle}
            type="button"
            className={onglet.cle === statut ? 'actif' : ''}
            onClick={() => setStatut(onglet.cle)}
          >
            {onglet.libelle}
            {onglet.cle && compteurs[onglet.cle] ? ` (${compteurs[onglet.cle]})` : ''}
          </button>
        ))}
      </div>

      {info && <p className="signal">{info}</p>}
      {erreur && <Erreur message={erreur} />}
      {!messages && !erreur && <Chargement quoi="des messages" />}
      {messages?.length === 0 && <Vide enfants="Rien dans cette file." />}

      <ul className="messages">
        {messages?.map((message) => (
          <li key={message.id}>
            <div className="ligne1">
              <strong>{message.objet ?? '(sans objet)'}</strong>
              <span className="badge">{LIBELLES_STATUT_INGESTION[message.statut]}</span>
            </div>
            <div className="meta">
              <span>{message.expediteur}</span>
              {message.source && <span>{message.source}</span>}
              {message.recuLe && (
                <span>{new Date(message.recuLe).toLocaleString('fr-FR')}</span>
              )}
              {message.tentatives > 0 && <span>{message.tentatives} tentative(s)</span>}
            </div>

            {message.donnees && (
              <p className="synthese">
                {[
                  [message.donnees.prenom, message.donnees.nom].filter(Boolean).join(' '),
                  message.donnees.telephone,
                  message.donnees.email,
                  message.donnees.intitulePublie,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Aucune donnée reconnue'}
              </p>
            )}
            {message.erreur && <p className="erreur">{message.erreur}</p>}

            {message.statut !== 'traite' && (
              <label className="rattachement">
                Rattacher à
                <select
                  defaultValue=""
                  onChange={(evenement) => {
                    if (evenement.target.value) {
                      void rattacher(message.id, evenement.target.value);
                      evenement.target.value = '';
                    }
                  }}
                >
                  <option value="">choisir une annonce…</option>
                  {annonces.map((annonce) => (
                    <option key={annonce.id} value={annonce.id}>
                      {libelleAnnonceRattachement(annonce)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
